import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import AppShellLayout from "./components/AppShellLayout";

// ── Public marketing pages ──
import Landing from "./pages/Landing";
import Download from "./pages/Download";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import ResetPassword from "./pages/ResetPassword";

// ── Auth screens (standalone, no marketing nav -- same as the desktop app) ──
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";

// ── The actual product, behind login ──
import Plan from "./pages/Plan";
import Monitor from "./pages/monitor/Monitor";
import Providers from "./pages/providers/Providers";
import Widget from "./pages/widget/Widget";
import Profile from "./pages/settings/Profile";

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Public marketing site -- unchanged, no auth awareness at all */}
        <Route element={<Layout />}>
          <Route path="/" element={<Landing />} />
          <Route path="/download" element={<Download />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
        </Route>

        {/* Reset-password stays standalone, outside both layouts -- this is
            what the emailed reset link opens, same as before. */}
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Auth screens -- standalone full-page cards, no marketing nav
            above them (matches the desktop app's design). Redirect away
            if already logged in. */}
        <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
        <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
        <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />

        {/* Plan selection -- authenticated, but outside the app sidebar
            shell (same as desktop: shown right after signup/login, before
            landing in the main app). */}
        <Route path="/plan" element={<RequireAuth><Plan /></RequireAuth>} />

        {/* The actual product */}
        <Route
          path="/app"
          element={
            <RequireAuth>
              <AppShellLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="monitor" replace />} />
          <Route path="monitor" element={<Monitor />} />
          <Route path="providers" element={<Providers />} />
          <Route path="widget" element={<Widget />} />
          <Route path="profile" element={<Profile />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}

function RequireAuth({ children }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) return <Navigate to="/app/monitor" replace />;
  return children;
}
